import clsx from "clsx";
import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { getPublicMemento } from "apis/magnets";
import { Tooltip } from "react-tooltip";
import { uniqueId } from "lodash";
import { get } from "utils/request";
// eslint-disable-next-line import/no-cycle
import IncentiveModal from "./IncentiveModal";

type Props = {
  incentiveData: IncentiveMemento;
  shareRequestsResponded: string[];
  pollsResponded: string[];
  isPreview: boolean;
};

function MementosList({ mementos }: { mementos: string[] }) {
  const [isLoading, setIsLoading] = useState(false);
  const [mementoData, setMementoData] = useState<{ [key: string]: Memento }>({});
  useEffect(() => {
    const memData: { [key: string]: Memento } = {};
    setIsLoading(true);
    const arrOfReq = mementos.map((mementoId) =>
      get(getPublicMemento(mementoId), {
        processData: ({ data }) => {
          memData[mementoId] = data;
        },
      }),
    );
    Promise.all(arrOfReq).then(() => {
      setMementoData(memData);
      setIsLoading(false);
    });
  }, [mementos]);
  if (isLoading) return <div className="three-dots-loading">Loading...</div>;
  return (
    <div className="text:xxs flex w-56 flex-col gap-2 sm:text-xs">
      {mementos.map((memId) => (
        <div className="flex gap-2 font-satoshi" key={memId}>
          <img
            className="inline-block aspect-1 h-10 object-cover sm:h-12"
            src={mementoData[memId]?.thumbnail_url}
            alt=""
          />
          <div className="flex flex-col">
            <span className="font-semibold">{mementoData[memId]?.name}</span>
            <span className="text-xxs opacity-75">
              {mementoData[memId]?.activation_key === "social_share" ? "Share Request" : "Poll"}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReadableCondition({ condition }: { condition: IncentiveConditionFromAPI }) {
  if (condition.action_type === "MIN_PARTICIPATED_COUNT") {
    return (
      <p className="text-xxs leading-tight sm:text-xs">
        {condition.activation_key === "poll" ? "Respond to" : "Complete"} any {condition.value}{" "}
        {condition.activation_key === "poll"
          ? `Poll${condition.value > 1 ? "s" : ""}`
          : `Share Request${condition.value > 1 ? "s" : ""}`}
      </p>
    );
  }
  const uniqueClassName = uniqueId("tooltip_");
  return (
    <div className="pr-2 text-xxs sm:text-xs">
      Complete{" "}
      <span className={`${uniqueClassName} cursor-pointer underline`}>
        {condition.value.length} Influence {condition.value.length > 1 ? "Opportunities" : "Opportunity"}
      </span>
      <Tooltip
        style={{ opacity: 1 }}
        delayHide={1000}
        clickable
        anchorSelect={`.${uniqueClassName}`}
        variant="dark"
        place="bottom"
      >
        <MementosList mementos={condition.value} />
      </Tooltip>
    </div>
  );
}

function getProgressForCondition(condition: IncentiveConditionFromAPI) {
  return {
    fraction:
      condition.user_fulfilled_count /
      (condition.action_type === "MIN_PARTICIPATED_COUNT" ? condition.value : condition.value.length),
    remaining:
      (condition.action_type === "MIN_PARTICIPATED_COUNT" ? condition.value : condition.value.length) -
      condition.user_fulfilled_count,
  };
}

// function getTotalProgressPercent(
//   incentiveRules: IncentiveRulesFromAPI,
//   pollsResponded: string[],
//   socialShareResponded: string[],
// ) {
//   if (incentiveRules.conditions.length === 1) {
//     const progress = getProgressForCondition(incentiveRules.conditions[0], pollsResponded, socialShareResponded);
//     return Math.floor(progress.fraction * 100);
//   }
//   const { fraction: fraction1, remaining: remaining1 } = getProgressForCondition(
//     incentiveRules.conditions[0],
//     pollsResponded,
//     socialShareResponded,
//   );
//   const { fraction: fraction2, remaining: remaining2 } = getProgressForCondition(
//     incentiveRules.conditions[1],
//     pollsResponded,
//     socialShareResponded,
//   );
//   if (incentiveRules.operator === "OR") {
//     const max = Math.max(fraction1, fraction2);
//     return Math.floor(max * 100);
//   }
//   const { conditions } = incentiveRules;
//   const actions0 =
//     conditions[0].action_type === "MIN_PARTICIPATED_COUNT" ? conditions[0].value : conditions[0].value.length;
//   const actions1 =
//     conditions[1].action_type === "MIN_PARTICIPATED_COUNT" ? conditions[1].value : conditions[1].value.length;
//   const totalActions = actions0 * 1 + actions1 * 1;
//   return Math.floor(((totalActions - remaining1 - remaining2) * 100) / totalActions);
// }

function ConditionProgress({
  condition,
}: // pollsResponded,
// socialShareResponded,
{
  condition: IncentiveConditionFromAPI;
  // pollsResponded: string[];
  // socialShareResponded: string[];
}) {
  const progress = getProgressForCondition(condition);
  const degree = Math.floor(progress.fraction * 360);
  return (
    <div className="flex items-center justify-evenly gap-2 pt-1">
      {progress.remaining === 0 ? (
        <div>
          <CheckCircleIcon className="-m-1 h-6 w-6 text-lime-500" />
        </div>
      ) : (
        <div
          style={{ background: `conic-gradient(rgb(132,204,22) ${degree}deg, transparent 0deg)` }}
          className="aspect-1 h-5 shrink-0 rounded-[50%] border-1 border-lime-500"
        />
      )}
      <p className="text-base">{progress.remaining === 0 ? "Completed" : `${progress.remaining} more to go`}</p>
    </div>
  );
}

function IncentivesCard({ incentiveData, shareRequestsResponded, pollsResponded, isPreview }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const percent = incentiveData.user_progress;
  return (
    <div
      className={clsx(
        "mt-2 inline-grid shrink-0 grid-cols-5 gap-1.5 rounded-lg",
        isPreview
          ? "incentive-card-background max-w-[12rem] p-1.5 text-primary hover:cursor-pointer xl:w-28"
          : "p-6 text-white",
      )}
      onClick={() => {
        setIsModalOpen(isPreview && true);
      }}
    >
      <img
        src={incentiveData.thumbnail_url}
        alt={`${incentiveData.name} thumbnail`}
        className={clsx("aspect-1 w-full rounded-lg object-cover", isPreview && "col-span-5")}
      />
      <div className={clsx(isPreview ? "col-span-5" : "col-span-4")}>
        <div
          title={incentiveData.name}
          className={clsx(
            "-mb-0.5 font-satoshi font-black leading-tight",
            isPreview ? "line-clamp-1 text-base" : "line-clamp-1 text-lg",
          )}
        >
          {incentiveData.name}
        </div>
        <Link
          to={`/brand-public-profile/${incentiveData.brand_id}`}
          title={incentiveData.brand_name}
          className={clsx(
            "font-satoshi font-medium",
            isPreview ? "line-clamp-1 text-[0.70rem] leading-normal opacity-80" : "text-xs",
          )}
        >
          {incentiveData.brand_name}
        </Link>
      </div>
      <div className={clsx("col-span-5 flex justify-between", isPreview ? "mt-1.5" : "mt-4")}>
        <span className="text-[0.60rem]">Progress</span>
        <span className="text-[0.60rem] text-base font-medium">{percent}%</span>
      </div>
      <div className={clsx("col-span-5 -mt-1.5 mb-2 w-full rounded-full bg-gray-700", isPreview ? "h-1" : "h-1.5")}>
        <div className="h-full rounded-full bg-blue-500" style={{ width: `${percent}%` }} />
      </div>
      {!isPreview && (
        <>
          <h2 className="col-span-5 mb-2 font-satoshi text-lg font-bold underline sm:text-xl">Goals</h2>
          {incentiveData.incentive_rules.conditions.map((condition, id) => (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={`incentive-condition-${id}`}>
              {id !== 0 && (
                <span className="col-span-5 mb-1 font-normal opacity-80">{incentiveData.incentive_rules.operator}</span>
              )}
              <div className="col-span-4 mb-2 font-medium">
                <ReadableCondition condition={condition} />
              </div>
              <div>
                <ConditionProgress
                  condition={condition}
                  // pollsResponded={pollsResponded}
                  // socialShareResponded={shareRequestsResponded}
                />
              </div>
            </Fragment>
          ))}
        </>
      )}
      {isModalOpen && (
        <IncentiveModal
          incentiveData={incentiveData}
          pollsResponded={pollsResponded}
          socialShareResponded={shareRequestsResponded}
          onClose={() => setIsModalOpen(false)}
          isOpen={isModalOpen}
        />
      )}
    </div>
  );
}
export default IncentivesCard;
